import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Hooks
import { useAppDispatch } from 'hooks/redux';
// Async
import PaymentsAsync from 'store/payments/paymentsAsync';
// Actions
import { reservationsActions } from 'store/reservations/reservationsSlice';
// types
import { AccountTypes, CheckTypes } from 'types/PaymentTypes';
// Mui
import {
  Button, DialogActions, DialogContent,
  TextField, Grid, ButtonGroup, Box, Typography, Checkbox
} from '@mui/material';
import {
  LoadingButton
} from '@mui/lab';
// Utilities
import { isFieldRequired, isValueLength } from 'utilities/Validation';

interface IFormData {
  accountNumber: string;
  routingNumber: string;
  accountType: AccountTypes;
  accountHolderType: CheckTypes;
  company?: string;
  firstName?: string;
  lastName?: string;
}

type Props = {
  reservationId: number,
  onClose: () => void,
}

const PaymentCheckForm:React.FC<Props> = ({ reservationId, onClose }) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<boolean>(false);

  const { control, handleSubmit, formState:{ errors }, watch } = useForm<IFormData>({
    defaultValues: {
      accountNumber: '',
      routingNumber: '',
      accountType: AccountTypes.Checking,
      accountHolderType: CheckTypes.Personal,
      company: '',
      firstName: '',
      lastName: '',
    }
  });

  const accountHolderTypeWatcher = watch('accountHolderType');

  const onSubmit = handleSubmit((data:IFormData) => {
    setIsLoading(true);

    const { company, firstName, lastName, ...nextData } = data;
    const newData: any = {...nextData};

    if (accountHolderTypeWatcher === CheckTypes.Business) {
      newData['company'] = company;
    } else {
      newData['firstName'] = firstName;
      newData['lastName'] = lastName;
    }
    
    const reservationPaymentData = {
      reservationId,
      iroquoisCheckPaymentData: {...newData},
    }
    dispatch(PaymentsAsync.createPayment({reservationPaymentData}))
      .unwrap()
      .then(() => dispatch(reservationsActions.markAsPaidReservation(reservationId)))
      .finally(() => setIsLoading(false));
  });

  return (
    <React.Fragment>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {/* accountNumber */}
              <Controller
                control={control} name="accountNumber"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    label="Account number"
                    type="text"
                    error={Boolean(errors.accountNumber)}
                    helperText={errors.accountNumber ? errors.accountNumber.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* routingNumber */}
              <Controller
                control={control} name="routingNumber"
                rules={{ required: isFieldRequired, validate: { minLength: (value:string) => isValueLength(value, 9) } }}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    size="small"
                    value={value}
                    onChange={(e:any) => onChange(e.target.value.slice(0,9))}
                    fullWidth
                    label="Routing number"
                    type="text"
                    error={Boolean(errors.routingNumber)}
                    helperText={errors.routingNumber ? errors.routingNumber.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {/* accountType */}
              <Controller
                control={control} name="accountType"
                render={({ field: { value, onChange } }) => (
                  <ButtonGroup aria-label="accountType button group" fullWidth>
                    <Button variant={value === AccountTypes.Checking ? 'contained' : 'outlined'} onClick={() => onChange(AccountTypes.Checking)}>{AccountTypes.Checking}</Button>
                    <Button variant={value === AccountTypes.Savings ? 'contained' : 'outlined'} onClick={() => onChange(AccountTypes.Savings)}>{AccountTypes.Savings}</Button>
                  </ButtonGroup>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {/* accountHolderType */}
              <Controller
                control={control} name="accountHolderType"
                render={({ field: { value, onChange } }) => (
                  <ButtonGroup aria-label="checkType button group" fullWidth>
                    <Button variant={value === CheckTypes.Personal ? 'contained' : 'outlined'} onClick={() => onChange(CheckTypes.Personal)}>{CheckTypes.Personal}</Button>
                    <Button variant={value === CheckTypes.Business ? 'contained' : 'outlined'} onClick={() => onChange(CheckTypes.Business)}>{CheckTypes.Business}</Button>
                  </ButtonGroup>
                )}
              />
            </Grid>
              {accountHolderTypeWatcher === CheckTypes.Business ? (
                <Grid item xs={12}>
                  {/* company */}
                  <Controller
                    control={control} name="company"
                    rules={{ required: isFieldRequired }}
                    render={({ field }) => (
                      <TextField
                        size="small"
                        {...field}
                        fullWidth
                        label="Company"
                        type="text"
                        error={Boolean(errors.company)}
                        helperText={errors.company ? errors.company.message : ''}
                        required
                      />
                    )}
                  />
                </Grid>
              ) : (
                <React.Fragment>
                  <Grid item xs={12} sm={6}>
                    {/* firstName */}
                    <Controller
                      control={control} name="firstName"
                      rules={{ required: isFieldRequired }}
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          fullWidth
                          label="First name"
                          type="text"
                          error={Boolean(errors.firstName)}
                          helperText={errors.firstName ? errors.firstName.message : ''}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {/* lastName */}
                    <Controller
                      control={control} name="lastName"
                      rules={{ required: isFieldRequired }}
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          fullWidth
                          label="Last name"
                          type="text"
                          error={Boolean(errors.lastName)}
                          helperText={errors.lastName ? errors.lastName.message : ''}
                          required
                        />
                      )}
                    />
                  </Grid>
                </React.Fragment>
              )}
          </Grid>
        </form>
      </DialogContent>
      <Box sx={{ height: '200px', overflowY: 'scroll', m: 2 }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
          PAYMENT TERMS AND CONDITIONS
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          AUTHORIZATION
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          By checking the "I accept the Terms and Conditions" checkbox below I authorize my bank to debit my specified account for the amount of my payment. This is a one-time payment which will occur on the next business day or as soon as practical thereafter. If my payment cannot be completed for any reason, including insufficient funds or error in the information which I submitted, I will retain the same liability, which is my sole responsibility, for payment as though I had not attempted to make the payment. I also understand that additional fees and penalties may be collected to the extent of applicable law.This transaction may not be disputed and is non-refundable. Dates cannot be altered.
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          RECEIPT
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          A receipt can be printed after payment is accepted which may serve as evidence of payment. If an email address is provided during the payment process, a receipt will be emailed to you after the payment is processed.
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          CONTACT
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          If for any reason you wish to make a change to the payment after submission, please contact Dash Realty Group as the first option for assistance at 407-913-3274. Dashweek is a brand of Dash Realty Group. It is possible that you may receive correspondence from Dash Realty Group representative. I agree to the terms and conditions.
        </Typography>
      </Box>
      <DialogActions>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox  checked={accepted} onChange={(_:any, checked: boolean) => setAccepted(checked)} />
          <Typography sx={{ fontSize: '14px' }}>
              I ACCEPT THE TERMS AND CONDITIONS
          </Typography>
        </label>
        <Box flexGrow="1" />
        <Box sx={{ minWidth: 'max-content' }}>
          <Button
            variant="text"
            color="primary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isLoading}
            onClick={onSubmit}
            variant="contained"
            color="primary"
            disabled={!accepted}
          >
            Pay
          </LoadingButton>
        </Box>
      </DialogActions>
    </React.Fragment>
  )
}

export default PaymentCheckForm;
